import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError, otherParty, type CallListItem, type AgentPresence } from '../api/client';
import { CallButton } from '../components/RecentCallsPanel';
import { displayName } from '../utils/display';

export function CallHistoryPage() {
  const [agentFilter, setAgentFilter] = useState<string>('all');

  const callsQuery = useQuery({
    queryKey: ['calls', agentFilter],
    queryFn: () => api.listCalls(agentFilter === 'all' ? undefined : agentFilter).then(r => r.calls),
  });

  const agentsQuery = useQuery({
    queryKey: ['agents-for-filter'],
    queryFn: () => api.getAgents().then(r => r.agents),
  });

  const agentList: AgentPresence[] | undefined = agentsQuery.data;

  return (
    <>
      <h2 className="text-lg font-semibold text-slate-900">Call history</h2>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            All calls across the team. Filter to a single agent if needed.
          </p>
          {agentList && (
            <label className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">Agent:</span>
              <select
                value={agentFilter}
                onChange={e => setAgentFilter(e.target.value)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5"
              >
                <option value="all">All agents</option>
                {agentList.map(a => (
                  <option key={a.userId} value={a.userId}>
                    {displayName(a)}{displayName(a) !== a.email ? ` (${a.email})` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {callsQuery.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
        {callsQuery.error && (
          <p className="text-sm text-red-700">
            {callsQuery.error instanceof ApiError ? `API error ${callsQuery.error.status}` : 'Failed to load calls.'}
          </p>
        )}

        {callsQuery.data && (
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">When</th>
                  <th className="text-left px-4 py-2.5">Number</th>
                  <th className="text-left px-4 py-2.5">Direction</th>
                  <th className="text-left px-4 py-2.5">Agent</th>
                  <th className="text-left px-4 py-2.5">Duration</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-right px-4 py-2.5"> </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {callsQuery.data.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400">No calls yet.</td></tr>
                )}
                {callsQuery.data.map(c => <CallRow key={`${c.agentUserId}-${c.startedAt}`} call={c} agents={agentList} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function CallRow({ call, agents }: { call: CallListItem; agents?: AgentPresence[] }) {
  const agent = agents?.find(a => a.userId === call.agentUserId);
  const agentLabel = agent ? displayName(agent) : call.agentUserId;
  const number = otherParty(call);
  const dir = call.direction ?? 'inbound';
  return (
    <tr>
      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatDateTime(call.startedAt)}</td>
      <td className="px-4 py-3 font-mono text-slate-900 whitespace-nowrap">{formatNumber(number)}</td>
      <td className="px-4 py-3 text-xs text-slate-600">{dir === 'outbound' ? 'outgoing' : 'incoming'}</td>
      <td className="px-4 py-3 text-slate-700" title={agent?.email}>{agentLabel}</td>
      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatDuration(call.durationSeconds)}</td>
      <td className="px-4 py-3"><StatusBadge status={call.status} /></td>
      <td className="px-4 py-3 text-right"><CallButton number={number} /></td>
    </tr>
  );
}

function StatusBadge({ status }: { status: CallListItem['status'] }) {
  const cls = status === 'completed' ? 'bg-emerald-100 text-emerald-800'
    : status === 'in-progress' ? 'bg-amber-100 text-amber-800'
    : 'bg-red-100 text-red-800';
  return <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{status}</span>;
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function formatDuration(seconds?: number): string {
  if (!seconds && seconds !== 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatNumber(raw: string): string {
  if (!raw) return '—';
  if (raw.startsWith('+61') && raw.length === 12) {
    return `${raw.slice(0, 3)} ${raw.slice(3, 6)} ${raw.slice(6, 9)} ${raw.slice(9)}`;
  }
  if (raw.startsWith('+1') && raw.length === 12) {
    return `${raw.slice(0, 2)} (${raw.slice(2, 5)}) ${raw.slice(5, 8)}-${raw.slice(8)}`;
  }
  return raw;
}
